import React from 'react';
import styled from '@emotion/styled';
import { i18n_docs_and_films } from '@yi/i18n/phrases/i18n_docs_and_films';
import { i18n_episodes } from '@yi/i18n/phrases/i18n_episodes';
import { i18n_new_episodes } from '@yi/i18n/phrases/i18n_new_episodes';

import { IconSeries } from '~/src/components/icons';
import { useLocale } from '~/src/components/LocaleProvider';
import { fontWeight, turquoise } from '~/src/styles';

import { BaseCard, BaseCardProps } from './bases';

export type Item = {
  episodeNumber: number;
  url: string;
  imageUrl: string;
  title: string;
  id: string;
  isNewEpisode: boolean;
};

interface Props extends Omit<Partial<BaseCardProps>, 'onClick'> {
  item: Item;
  noHover?: boolean;
  onClick?(i: Item): void;
}

const Title = styled.div`
  display: flex;
  gap: 8px;
  color: #fff;
  font-size: 12px;
  line-height: 20px;
  font-weight: ${fontWeight.bold};
  margin: 6px 0 0 0;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const EpisodeNumber = styled.div`
  font-size: 12px;
  font-weight: ${fontWeight.medium};
  line-height: 24px;
  letter-spacing: 0.05em;
  text-align: right;
  color: white;
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
`;
const NewEpisodesLabel = styled.div`
  font-size: 12px;
  font-weight: ${fontWeight.medium};
  line-height: 24px;
  letter-spacing: 0.05em;
  text-align: left;
  text-transform: uppercase;
  background: ${turquoise};
  color: white;
  padding: 2px 16px 2px 10px;
  width: fit-content;
  border-bottom-right-radius: 20px;
  border-top-right-radius: 20px;
  transform: translate(-10px, 0);
`;

const defaultDimension = {
  width: 320,
  height: 320,
};

export function isRecentSeriesEpisodeCardItem(item: unknown): item is Item {
  return (
    typeof item === 'object' &&
    item !== null &&
    'url' in item &&
    typeof item.url === 'string' &&
    /\/series\//.test(item.url)
  );
}

export const RecentSeriesActivityCard: React.FC<Props> & {
  dimension: { width: number; height: number };
} = ({ item, noHover = true, onClick, ...props }) => {
  const { locale } = useLocale();

  const { imageUrl, episodeNumber, url, title } = item;
  const thumbLabel = `"${title}" course card background image`;
  const imageHeight = 194;

  const dimension = defaultDimension;
  return (
    <BaseCard
      onClick={() => {
        if (typeof onClick === 'function') {
          onClick(item);
        }
      }}
      dimension={dimension}
      hoverable={!noHover}
      url={url}
      thumb={imageUrl}
      thumbLabel={thumbLabel}
      imageHeight={imageHeight}
      bgImageFull
      renderUpperLeft={() => (
        <TitleContainer>
          <Title>
            <IconSeries
              style={{
                width: '15px',
                height: 'auto',
                color: '#fff',
              }}
            />
            {i18n_docs_and_films(locale)}
          </Title>
          {item.isNewEpisode && <NewEpisodesLabel>{i18n_new_episodes(locale)}</NewEpisodesLabel>}
        </TitleContainer>
      )}
      renderLowerRight={() => (
        <EpisodeNumber>
          {episodeNumber} {i18n_episodes(locale)}
        </EpisodeNumber>
      )}
      {...props}
    />
  );
};

RecentSeriesActivityCard.dimension = defaultDimension;
